import { Injectable, Injector } from '@angular/core'
import { OAuthService, OAuthEvent } from 'angular-oauth2-oidc'
import { authConfig } from '../config/auth-config'
import { BehaviorSubject } from 'rxjs'
import { environment } from 'src/environments/environment'
import { UnespCoreMessageService } from 'src/libs/unesp-core'
import { UnespCoreAuthService } from 'src/libs/unesp-core/services/unesp-core-auth/unesp-core-auth.service'

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private static readonly ID_TOKEN_BACKUP_KEY = '__sinsc_id_token_hint_backup'
  private static readonly INACTIVITY_TIMEOUT_MS = environment.inativeTimeInSeconds * 1000 // 30 min
  private static readonly WARNING_START_SECONDS = 180 // avisos começam 3 min antes

  private refreshTimer?: ReturnType<typeof setTimeout>
  private inactivityTimer?: ReturnType<typeof setInterval>
  private activityHandler?: () => void
  private lastWarningMinute = 0
  private expirationRefreshTokenIntervalInSeconds = environment.expirationRefreshTokenIntervalInSeconds
  private tokenRefreshed = new BehaviorSubject<boolean>(false)
  private logoutSubject = new BehaviorSubject<void>(undefined)
  public logout$ = this.logoutSubject.asObservable()
  private lastIdToken: string | null = null
  private refreshPromise: Promise<string> | null = null
  private isLoggingOut = false
  private lastActivity = Date.now()

  constructor(
    private oauthService: OAuthService,
    private messageService: UnespCoreMessageService,
    private injector: Injector
  ) {
    this.messageService = messageService
    this.oauthService.setStorage(localStorage)
    this.lastIdToken = localStorage.getItem(AuthService.ID_TOKEN_BACKUP_KEY)
    setTimeout(() => {
      this.configure()
    })
  }

  private configure() {
    this.oauthService.configure(authConfig)
    if (window.opener && location.pathname.includes('silent-refresh')) return
    this.oauthService.loadDiscoveryDocumentAndTryLogin({ disableOAuth2StateCheck: true }).then(() => {
      this.backupIdToken()
    })

    this.oauthService.events.subscribe((event: OAuthEvent) => {
      if (event.type === 'token_received') {
        this.backupIdToken()
        this.tokenRefreshed.next(true)
        this.startRefreshTokenTimeout(this.getExpirationToken)
        this.startInactivityMonitor()
      } else if (
        (event.type === 'token_refresh_error' || event.type === 'token_expires') &&
        this.oauthService.getRefreshToken() == null
      ) {
        this.clearRefreshTimer()
      }
    })
  }

  /** Registra atividade do usuário */
  registerActivity() {
    this.lastActivity = Date.now()
    this.lastWarningMinute = 0
  }

  private startInactivityMonitor() {
    // Evita registrar listeners duplicados
    if (this.inactivityTimer) return

    this.lastActivity = Date.now()
    this.lastWarningMinute = 0

    // Escuta eventos de interação do usuário (uma única vez)
    if (!this.activityHandler) {
      this.activityHandler = () => this.registerActivity()
      const events = ['mousedown', 'keydown', 'scroll', 'touchstart']
      events.forEach(e => document.addEventListener(e, this.activityHandler!, { passive: true }))
    }

    // Verifica inatividade a cada 10 segundos
    this.inactivityTimer = setInterval(() => {
      if (!this.isLoggedIn || this.isLoggingOut) return

      const inactiveMs = Date.now() - this.lastActivity
      const remainingMs = AuthService.INACTIVITY_TIMEOUT_MS - inactiveMs
      const remainingSeconds = Math.floor(remainingMs / 1000)

      if (remainingMs <= 0) {
        this.clearInactivityTimer()
        this.injector.get(UnespCoreAuthService).logout('Sessão expirada por inatividade', true, 0)
      } else if (remainingSeconds <= AuthService.WARNING_START_SECONDS) {
        // Avisos de minuto a minuto: 3 min, 2 min, 1 min
        const currentMinute = Math.ceil(remainingSeconds / 60)
        if (currentMinute !== this.lastWarningMinute && currentMinute >= 1 && currentMinute <= 3) {
          this.lastWarningMinute = currentMinute
          this.messageService.showMessageWarning(
            `Sua sessão irá expirar em ${currentMinute} minuto${currentMinute > 1 ? 's' : ''} por inatividade.`,
            'Entendi',
            10000
          )
        }
      }
    }, 10000)
  }

  private clearInactivityTimer() {
    if (this.inactivityTimer) {
      clearInterval(this.inactivityTimer)
      this.inactivityTimer = undefined
    }
    this.lastWarningMinute = 0
  }

  private backupIdToken(): void {
    const idToken = this.oauthService.getIdToken()
    if (idToken) {
      this.lastIdToken = idToken
      localStorage.setItem(AuthService.ID_TOKEN_BACKUP_KEY, idToken)
    }
  }

  login(targetUrl?: string) {
    this.oauthService
      .loadDiscoveryDocument()
      .then(() =>
        this.oauthService.tryLogin().then(() => {
          if (!this.oauthService.hasValidAccessToken()) {
            if (targetUrl) {
              this.oauthService.initCodeFlow(targetUrl)
            } else {
              this.oauthService.initCodeFlow()
            }
          }
        })
      )
      .catch(error => {
        console.error('Login error:', error)
      })
  }

  logout() {
    this.isLoggingOut = true
    this.logoutSubject.next()

    const idTokenFromStorage = this.oauthService.getIdToken()
    const idToken = idTokenFromStorage || this.lastIdToken

    if (idToken) {
      this.oauthService.logOut({ id_token_hint: idToken })
    } else {
      this.oauthService.logOut(true)
      window.location.href = window.location.origin
    }

    this.oauthService?.stopAutomaticRefresh()
    this.clearRefreshTimer()
    this.clearInactivityTimer()
    this.lastIdToken = null
    localStorage.removeItem(AuthService.ID_TOKEN_BACKUP_KEY)
    this.refreshPromise = null
    sessionStorage.clear()
    this.clearOAuthStorage()
  }

  /** Limpa sessão local sem redirecionar para a AC */
  logoutLocal() {
    this.isLoggingOut = true
    this.logoutSubject.next()
    this.oauthService.logOut(true)
    this.oauthService?.stopAutomaticRefresh()
    this.clearRefreshTimer()
    this.clearInactivityTimer()
    this.lastIdToken = null
    localStorage.removeItem(AuthService.ID_TOKEN_BACKUP_KEY)
    this.refreshPromise = null
    sessionStorage.clear()
    this.clearOAuthStorage()
  }

  private clearOAuthStorage(): void {
    const oauthKeys = [
      'access_token', 'id_token', 'refresh_token', 'expires_at',
      'id_token_claims_obj', 'id_token_expires_at', 'id_token_stored_at',
      'access_token_stored_at', 'granted_scopes', 'session_state', 'nonce', 'PKCE_verifier',
    ]
    oauthKeys.forEach(key => localStorage.removeItem(key))
    const prefix = environment.localStoragePrefix
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key))
  }

  forceAuth() {
    this.oauthService.customQueryParams = { prompt: 'login' }
    return this.oauthService
      .initLoginFlowInPopup({ height: 700, width: 800 })
      .then(() => {
        this.oauthService.customQueryParams = undefined
      })
  }

  public startRefreshTokenTimeout(expiresIn: number) {
    this.clearRefreshTimer()

    const refreshInMs = (expiresIn - this.expirationRefreshTokenIntervalInSeconds) * 1000

    if (refreshInMs <= 0) {
      this.refreshNow()
      return
    }

    this.refreshTimer = setTimeout(() => {
      this.refreshNow()
    }, refreshInMs)
  }

  /**
   * Refresh proativo — só faz refresh se o usuário esteve ativo recentemente.
   * Se inativo, deixa o token expirar naturalmente (o inactivityTimer cuida do logout).
   */
  public refreshNow() {
    const inactiveMs = Date.now() - this.lastActivity
    if (inactiveMs >= AuthService.INACTIVITY_TIMEOUT_MS) {
      // Usuário inativo — não renovar, deixar expirar
      return
    }

    this.refreshTokenAndGetNew().catch(error => {
      console.error('Error refreshing token:', error)
      this.clearRefreshTimer()
      this.messageService.showMessageError('Sua sessão expirou. Por favor, faça login novamente.')
      setTimeout(() => {
        this.injector.get(UnespCoreAuthService).logout('Sessão expirada', true, 0)
      }, 3000)
    })
  }

  /**
   * Refresh unificado: usado pelo timer, pelo interceptor e pelo visibilitychange.
   * Se já houver um refresh em andamento, retorna a mesma Promise (evita refreshes simultâneos).
   */
  public refreshTokenAndGetNew(): Promise<string> {
    if (this.refreshPromise) {
      return this.refreshPromise
    }

    this.refreshPromise = this.oauthService
      .refreshToken()
      .then(() => {
        this.refreshPromise = null
        if (this.isLoggingOut) return this.oauthService.getAccessToken()
        this.backupIdToken()
        const newExpiresIn = this.getExpirationToken
        this.startRefreshTokenTimeout(newExpiresIn)
        return this.oauthService.getAccessToken()
      })
      .catch(error => {
        this.refreshPromise = null
        throw error
      })

    return this.refreshPromise
  }

  private clearRefreshTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = undefined
    }
  }

  get isLoggedIn(): boolean {
    return this.oauthService.hasValidAccessToken()
  }

  get oauthEvent() {
    return this.oauthService.events
  }

  get getAccessToken() {
    return this.oauthService.getAccessToken()
  }

  get getIdToken() {
    return this.oauthService.getIdToken()
  }

  get getTokenExpiration() {
    return this.oauthService.getAccessTokenExpiration()
  }

  get getGrantedScopes() {
    return this.oauthService.getGrantedScopes()
  }

  get getRefreshToken() {
    return this.oauthService.getRefreshToken()
  }

  get tokenRefreshed$() {
    return this.tokenRefreshed.asObservable()
  }

  get getExpirationToken() {
    const expiresAt = this.oauthService.getAccessTokenExpiration()
    const now = new Date().getTime()
    const expiresIn = Math.floor((expiresAt - now) / 1000)
    return expiresIn
  }

  get oauthState() {
    return this.oauthService.state
  }
}
